'use client';

import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CurrencyFilters } from '@/components/AddLiquidity/AddLiquidityProvider';

/**
 * Parameters for opening the Add Liquidity modal
 */
export type AddLiquidityModalParams = {
  /** Pool ID to add liquidity to (for new positions from pool details page) */
  poolId?: string;
  /** Position ID to add liquidity to (for increasing existing positions) */
  positionId?: string;
  /** Position manager address for position lookup */
  positionManagerAddress?: `0x${string}`;
  /** Currency filters to limit pool selection */
  currencyFilters?: CurrencyFilters;
};

type AddLiquidityModalState = {
  isOpen: boolean;
  params: AddLiquidityModalParams | null;
};

type AddLiquidityModalContextType = {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Parameters passed when opening the modal */
  params: AddLiquidityModalParams | null;
  /** Open the Add Liquidity modal with optional parameters */
  openAddLiquidityModal: (params?: AddLiquidityModalParams) => void;
  /** Close the Add Liquidity modal */
  closeAddLiquidityModal: () => void;
};

const AddLiquidityModalContext = createContext<AddLiquidityModalContextType | null>(null);

/**
 * Hook to access the Add Liquidity modal context
 * @throws Error if used outside of AddLiquidityModalProvider
 */
export function useAddLiquidityModal(): AddLiquidityModalContextType {
  const context = useContext(AddLiquidityModalContext);
  if (!context) {
    throw new Error('useAddLiquidityModal must be used within AddLiquidityModalProvider');
  }
  return context;
}

type AddLiquidityModalProviderProps = {
  children: ReactNode;
};

/**
 * Provider for managing the Add Liquidity modal state
 * Should be placed high in the component tree (in ClientLayout)
 */
export const AddLiquidityModalProvider = memo(function AddLiquidityModalProvider({
  children,
}: AddLiquidityModalProviderProps) {
  const [state, setState] = useState<AddLiquidityModalState>({
    isOpen: false,
    params: null,
  });

  const openAddLiquidityModal = useCallback((params?: AddLiquidityModalParams) => {
    setState({
      isOpen: true,
      params: params ?? null,
    });
  }, []);

  const closeAddLiquidityModal = useCallback(() => {
    setState({
      isOpen: false,
      params: null,
    });
  }, []);

  const contextValue = useMemo<AddLiquidityModalContextType>(
    () => ({
      isOpen: state.isOpen,
      params: state.params,
      openAddLiquidityModal,
      closeAddLiquidityModal,
    }),
    [state.isOpen, state.params, openAddLiquidityModal, closeAddLiquidityModal],
  );

  return (
    <AddLiquidityModalContext.Provider value={contextValue}>
      {children}
    </AddLiquidityModalContext.Provider>
  );
});
